import { useState, type ReactElement } from "react";
import { Alert, Button, Modal, Select } from "@playbookai/ui";
import {
  LIFECYCLE_LABEL,
  RISK_LABEL,
  serverHint,
  riskDraftNote,
  type RiskDraft,
  planReview,
  reviewOutcomeNotice,
  toolLabel,
  type ReviewCandidate,
  type ReviewableLifecycle,
} from "./tool-catalog-review.js";

export interface ToolCatalogReviewDialogProps {
  readonly open: boolean;
  /** The rows the administrator chose, with the class each control is showing. */
  readonly candidates: readonly ReviewCandidate[];
  readonly pending: boolean;
  /**
   * How the last confirmation actually settled. Both halves are needed: the
   * writes are independent, so "some failed" does NOT mean "none applied".
   */
  readonly savedCount: number;
  readonly failedCount: number;
  /**
   * How many of those failures were "the tool changed while you were reviewing
   * it". A subset of `failedCount`, called out because it is the one refusal
   * whose next step is to look again rather than to retry.
   */
  readonly staleCount: number;
  readonly onClassificationChange?: (id: string, risk: RiskDraft) => void;
  readonly onCancel: () => void;
  readonly onConfirm: (input: {
    readonly lifecycle: ReviewableLifecycle;
    readonly entries: readonly ReviewCandidate[];
  }) => void;
}

/**
 * The confirmation step between a selection and a policy change.
 *
 * It exists because none of the controls behind it may act on their own: ticking
 * a row, or moving a classification dropdown, must never enable a tool or reach
 * the review API (ADR-0071 — a remote tool is unusable until a tenant
 * administrator reviews it AND enables it). This dialog is where those two
 * separate decisions are stated together, once, and where the rows the decision
 * CANNOT be written to are named before the request rather than after it.
 */
export function ToolCatalogReviewDialog({
  open,
  candidates,
  pending,
  savedCount,
  failedCount,
  staleCount,
  onCancel,
  onClassificationChange,
  onConfirm,
}: ToolCatalogReviewDialogProps): ReactElement {
  /* Defaults to Enabled — the decision this queue exists to make — and resets
     to it every time the dialog opens, so a previous "Disabled" cannot be
     inherited by the next selection an administrator confirms without reading.
     Adjusted during render rather than in an effect, the same idiom AuditAdmin's
     pager uses for its own reset key. */
  const [lifecycle, setLifecycle] = useState<ReviewableLifecycle>("enabled");
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setLifecycle("enabled");
  }

  const plan = planReview(candidates, lifecycle);
  const outcome = reviewOutcomeNotice(savedCount, failedCount, staleCount);

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="lg"
      title={
        candidates.length === 1
          ? "Review this tool"
          : `Review ${candidates.length} tools`
      }
      description="Your confirmed classification is what governs. Server annotations are unverified suggestions and grant nothing."
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm({ lifecycle, entries: plan.apply })}
            disabled={pending || plan.apply.length === 0}
          >
            {pending ? "Saving…" : "Confirm review"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select
          label="Workspace access"
          hint="Enabled tools become available automatically when a member connects an account."
          value={lifecycle}
          options={[
            { value: "enabled", label: LIFECYCLE_LABEL.enabled },
            { value: "disabled", label: LIFECYCLE_LABEL.disabled },
            { value: "quarantined", label: LIFECYCLE_LABEL.quarantined },
          ]}
          onChange={(next) => setLifecycle(next as ReviewableLifecycle)}
        />

        <ul className="max-h-80 divide-y divide-pb-border overflow-y-auto rounded-lg border border-pb-border">
          {candidates.map(({ entry, riskClass }) => {
            const hint = serverHint(entry.annotations);
            const note = riskDraftNote(entry, riskClass ?? "");
            return (
              <li
                key={entry.id}
                className="flex flex-wrap items-center justify-between gap-2 p-3 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <p className="break-words font-medium">{toolLabel(entry)}</p>
                  <p className="text-pb-text-muted">
                    {hint === null ? "No annotation" : `${hint} · Unverified`}
                  </p>
                  {note ? (
                    <p className="text-pb-amber-text">
                      {note.replace(" — confirm to apply", "")}
                    </p>
                  ) : null}
                </div>
                {onClassificationChange ? (
                  <div className="w-40">
                    <Select
                      ariaLabel={`Classification for ${toolLabel(entry)}`}
                      value={riskClass ?? ""}
                      disabled={pending || entry.lifecycle === "withdrawn"}
                      options={[
                        { value: "", label: "Unclassified" },
                        { value: "read", label: "Read" },
                        { value: "write", label: "Write" },
                      ]}
                      onChange={(next) =>
                        onClassificationChange(
                          entry.id,
                          next === "read" || next === "write" ? next : "",
                        )
                      }
                    />
                  </div>
                ) : null}
                <span className="sr-only">
                  {riskClass === null ? "Unclassified" : RISK_LABEL[riskClass]}{" "}
                  · {LIFECYCLE_LABEL[lifecycle]}
                </span>
              </li>
            );
          })}
        </ul>

        {/* Named, not silently dropped: a selection that shrinks without
            explanation reads as a bug, and each of these is something the
            administrator can act on. */}
        {plan.skipped.length > 0 ? (
          <Alert
            tone="watch"
            title={
              plan.skipped.length === 1
                ? "1 tool will not change"
                : `${plan.skipped.length} tools will not change`
            }
          >
            <ul className="space-y-1">
              {plan.skipped.map(({ entry, reason }) => (
                <li key={entry.id}>
                  <span className="font-medium">{toolLabel(entry)}</span> —{" "}
                  {reason}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}

        {/* Truthful about a settled-independently bulk write: the entries that
            succeeded are already live policy, so this must not claim the whole
            thing rolled back. See `reviewOutcomeNotice`. */}
        {failedCount > 0 ? (
          <Alert tone="critical" title={outcome.title}>
            {outcome.body}
          </Alert>
        ) : null}
      </div>
    </Modal>
  );
}
